import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { SESSION_QUERY_KEY, useSession } from '@platform/api-client/react';
import { Button, Dialog, formatRemaining } from '@platform/ui';

/** How long before `expiresAt` the warning appears. */
export const WARNING_WINDOW_SECONDS = 120;

export interface SessionExpiryWatcherProps {
  /** Injected by tests so the countdown is deterministic. */
  now?: () => number;
}

/**
 * The session expiry warning (M2.7, SECURITY.md §4.4, FR-ADMIN-01).
 *
 * A staff session is short on purpose, and this is the app where being silently signed
 * out mid-edit costs the most: a half-written patient note or a reschedule that was
 * about to be confirmed. Two minutes before `expiresAt` the staff member is told, and
 * offered the one action that fixes it.
 *
 * ## Why "Stay signed in" just refetches the session
 *
 * Idle expiry is measured from `session.updated_at`, which every authenticated request
 * moves. There is no "extend session" endpoint and there should not be one: a button
 * that extends a session without the user doing anything is a button that keeps an
 * unattended front-desk machine signed in. Refetching `GET /auth/session` is a real
 * request from a real click, which is exactly what the idle timer is supposed to
 * measure.
 *
 * ## `SESSION_EXPIRED`
 *
 * When the session ends anyway - here, or on any other call - the `401` (code
 * `UNAUTHENTICATED` or `SESSION_EXPIRED`, which `ApiError.isUnauthenticated` treats
 * alike) puts the cache back to anonymous through the client's `onUnauthenticated`, and
 * `RequireAuth` redirects to `/login?next=…`. Nothing here navigates; one thing moving
 * the user is enough.
 */

/** The wall clock, as the rest of the platform's front end spells it (TESTING.md §2.1). */
const DEFAULT_NOW = (): number => new Date().getTime();

export function SessionExpiryWatcher({ now = DEFAULT_NOW }: SessionExpiryWatcherProps) {
  const session = useSession();
  const queryClient = useQueryClient();
  const expiresAt = session.data?.authenticated === true ? session.data.expiresAt : null;

  /*
   * The clock is the state and the remaining time is derived from it, so that a session
   * which moves - because "Stay signed in" worked - is reflected on the next render
   * rather than through a second effect pass.
   */
  const [nowMs, setNowMs] = useState(now);

  /*
   * Dismissal is recorded *against an expiry*, not as a bare boolean. A new `expiresAt`
   * is a different session window, so the earlier dismissal is spent and the warning may
   * appear again - with no effect needed to reset it.
   */
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
    // `now` is a test seam and stable in the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const at = expiresAt === null ? Number.NaN : Date.parse(expiresAt);
  const remaining = Number.isNaN(at) ? null : Math.max(0, Math.round((at - nowMs) / 1000));

  const open =
    remaining !== null &&
    remaining <= WARNING_WINDOW_SECONDS &&
    remaining > 0 &&
    dismissedFor !== expiresAt;

  return (
    <Dialog
      open={open}
      onClose={() => {
        setDismissedFor(expiresAt);
      }}
      dismissOnBackdrop={false}
      title="You will be signed out soon"
      description={
        remaining === null
          ? undefined
          : `This session ends in ${formatRemaining(remaining)}. Anything you have not saved will be lost.`
      }
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              setDismissedFor(expiresAt);
            }}
          >
            Dismiss
          </Button>
          <Button
            onClick={() => {
              setDismissedFor(expiresAt);
              void queryClient.invalidateQueries({
                queryKey: SESSION_QUERY_KEY,
                refetchType: 'all',
              });
            }}
          >
            Stay signed in
          </Button>
        </>
      }
    />
  );
}
